import { PasswordResetData } from "@/domain/entities-new/user/user.entity";
import {
  UserValidator,
  UserPreferencesValidator,
} from "@/domain/entities-new/user/user.validations";
import {
  CreateUserPreferencesError,
  DeleteUserPreferencesError,
  GetUserError,
  GetUserPreferencesError,
  PasswordResetError,
  PasswordResetTriggerError,
  UpdateUserError,
  UpdateUserPreferencesError,
  UserNotFoundError,
  UserPreferencesNotFoundError,
} from "@/domain/errors/users";
import { IUserRepository, IUserPreferencesRepository } from "@/domain/repositories/user.repository";
import APIClient, { APIClientWithSoftAuth } from "@/infrastructure/api/client";
import { repositoryErrorHandler } from "@/infrastructure/repositories/common/repository-error-handler";

// * User

const UserApiRepository: IUserRepository = {
  async getCurrent() {
    try {
      const response = await APIClientWithSoftAuth.get("/users/");
      return UserValidator.User.fromAPI(response.data[0]);
    } catch (error) {
      throw repositoryErrorHandler(
        error,
        new GetUserError(),
        new UserNotFoundError()
      );
    }
  },

  async getByID(id) {
    try {
      const response = await APIClient.get(`/users/${id}/`);
      return UserValidator.User.fromAPI(response.data);
    } catch (error) {
      throw repositoryErrorHandler(
        error,
        new GetUserError(),
        new UserNotFoundError()
      );
    }
  },

  async updateByID(id, userData) {
    try {
      await APIClient.put(`/users/${id}/`, userData);
    } catch (error) {
      throw repositoryErrorHandler(
        error,
        new UpdateUserError(),
        new UserNotFoundError()
      );
    }
  },

  async triggerReset(email: string) {
    try {
      await APIClient.post("/users/password-reset/triggers/", { email });
    } catch (error) {
      throw repositoryErrorHandler(
        error,
        new PasswordResetTriggerError(),
      );
    }
  },

  async resetPassword(data: PasswordResetData) {
    try {
      await APIClient.post("/users/password-reset/", {
        email: data.email,
        password: data.password,
        token: data.token,
      });
    } catch (error) {
      throw repositoryErrorHandler(
        error,
        new PasswordResetError(),
      );
    }
  },
};

// * User Preferences

const UserPreferencesApiRepository: IUserPreferencesRepository = {
  async getByUserID(userID) {
    try {
      const response = await APIClient.get(`/users/${userID}/preferences/`);
      return UserPreferencesValidator.UserPreferences.fromAPI(response.data);
    } catch (error) {
      throw repositoryErrorHandler(
        error,
        new GetUserPreferencesError(),
        new UserPreferencesNotFoundError()
      );
    }
  },

  async create(preferences) {
    try {
      await APIClient.post("/users/preferences/", preferences);
    } catch (error) {
      throw repositoryErrorHandler(error, new CreateUserPreferencesError());
    }
  },

  async deleteByID(id) {
    try {
      await APIClient.delete(`/users/preferences/${id}/`);
    } catch (error) {
      throw repositoryErrorHandler(
        error,
        new DeleteUserPreferencesError(),
        new UserPreferencesNotFoundError()
      );
    }
  },

  async updateByID(id, preferences) {
    try {
      await APIClient.put(`/users/preferences/${id}/`, preferences);
    } catch (error) {
      throw repositoryErrorHandler(
        error,
        new UpdateUserPreferencesError(),
        new UserPreferencesNotFoundError()
      );
    }
  },
};

export { UserApiRepository, UserPreferencesApiRepository };
